<template>
  <div class="col-auto edit-menu flex-center">
    <b-dropdown
      variant="link"
      toggle-class="edit-menu-button text-dark text-decoration-none overflow-button"
      menu-class="overflow-menu"
      class="overflow-menu-wrapper"
      :data-kr-id="kr.id"
      :lazy="true"
      no-caret
    >
      <template slot="button-content">
        <i class="overflow-menu-icon"></i>
      </template>
      <template v-if="kr.isArchived">
        <b-dropdown-item @click="triggerConfirmation('unarchive')">Restore KR</b-dropdown-item>
        <b-dropdown-item @click="triggerConfirmation('delete')">Delete KR</b-dropdown-item>
      </template>
      <template v-else>
        <b-dropdown-item @click.stop="goToKrPage">
          <action-key
            action="Open KR page"
            :key-combination="showKeyboardHints ? ['Shift', '<right>'] : []"
          />
        </b-dropdown-item>
        <b-dropdown-item @click.stop="editGoal(kr.id)">
          Edit KR
        </b-dropdown-item>
        <b-dropdown-item @click.stop="openCheckinModal">
          <action-key action="Check-in" :key-combination="showKeyboardHints ? ['space'] : []" />
        </b-dropdown-item>
        <b-dropdown-item @click.stop="showBulkCheckin">
          Check-in to all KRs
        </b-dropdown-item>
        <b-dropdown-item @click.stop="requestUpdate">
          Request an update
        </b-dropdown-item>
        <b-dropdown-item
          @click.stop="showGoogleSheetModal = true"
          v-if="kr.measureBy !== KR_MEASURING_UNIT.BOOLEAN"
        >
          {{
            kr.extLookup === EXT_LOOKUP.GOOGLE_SPREADSHEET
              ? 'Manage Google sheets'
              : 'Link to Google sheets'
          }}
        </b-dropdown-item>
        <b-dropdown-divider />
        <b-dropdown-item @click="showMoveKrModal = true">Move KR</b-dropdown-item>
        <b-dropdown-divider />
        <b-dropdown-item @click="showAwardModal">
          <div class="d-flex justify-content-between align-items-center">
            <span>Give an award</span>
            <i class="award-icon options-menu-icon"></i>
          </div>
        </b-dropdown-item>
        <b-dropdown-divider />
        <all-initiatives-nav-option type="KR" :kr="kr" :goal="goal" />
        <b-dropdown-divider />
        <follow-option :kr-id="kr.id" />
        <b-dropdown-divider />
        <b-dropdown-item @click="triggerConfirmation('reopen')" v-if="kr.isClosed"
          >Re-open KR
        </b-dropdown-item>
        <b-dropdown-item @click="isCloseKRModalShown = true" v-else>Close KR </b-dropdown-item>
        <b-dropdown-item @click="triggerConfirmation('archive')">Archive KR </b-dropdown-item>
      </template>
    </b-dropdown>
    <close-entity
      :entity="kr"
      v-if="isCloseKRModalShown"
      v-model="isCloseKRModalShown"
      @closed="$emit('kr-closed')"
    />
    <bulk-check-in
      :source-goal-id="kr.goalId"
      :source-kr-id="kr.id"
      :source="source"
      v-if="isBulkCheckinModalShown"
      v-model="isBulkCheckinModalShown"
      :org-id="kr.organizationId"
    />
    <move-kr v-if="showMoveKrModal" v-model="showMoveKrModal" :kr="kr" @moved="onKrMove" />
    <kr-google-connect
      :show.sync="showGoogleSheetModal"
      :kr="kr"
      :org-id="kr.organizationId"
      :goal-id="kr.goalId"
    ></kr-google-connect>
    <give-award
      v-model="isAwardModalShown"
      v-if="isAwardModalShown"
      :context="awardContext"
      :team-id="kr.teamId"
      @awarded="$emit('awarded')"
    />
    <confirm-dialog
      ref="confirmModal"
      :title="confirmationMessage.title"
      @ok="onConfirmation"
      :ok-title="confirmationMessage.ok"
      :ok-variant="action === 'delete' ? 'danger' : 'primary'"
      :cancel-title="confirmationMessage.cancel || 'No, I changed my mind'"
    >
      {{ confirmationMessage.body }}
      <div class="mt-3" v-if="confirmationMessage.bodySecondary">
        <b>Goal: </b> {{ confirmationMessage.bodySecondary }}
      </div>
    </confirm-dialog>
  </div>
</template>

<script>
import GiveAward from '@/components/award/GiveAward.vue'
import CloseEntity from '@/components/CloseEntity'
import FollowOption from '@/components/goal/FollowOption'
import KrActivityModal from '@/components/kr/KrActivityModal.vue'
import { ENTITY_TYPE } from '@/constants/entities'
import ParentGoalsMixin from '@/mixins/ParentGoalsMixin'
import service from '@/service'
import urlManager from '@/urlManager'
import getEntityAwardContext from '@/utils/getEntityAwardContext'
import handleClickNavigation from '@/utils/handleClickNavigation'
import { getEditGoalPageRouteConfig, getKrPageRouteConfig } from '@/utils/navigationConfigs'
import showAPIError from '@/utils/showAPIError'
import showModal from '@/utils/showModal'
import showToast from '@/utils/showToast'
import { BDropdown, BDropdownDivider, BDropdownItem } from 'bootstrap-vue'
import * as R from 'ramda'
import { mapActions } from 'vuex'
import { trackEvent } from '@/analytics'
import ActionKey from '@/components/ActionKey'
import AllInitiativesNavOption from '@/components/goal/AllInitiativesNavOption'
import BulkCheckIn from '@/components/BulkCheckIn'
import ConfirmDialog from '@/components/ConfirmDialog'
import KrGoogleConnect from '@/components/KrGoogleConnect'
import MoveKr from '@/components/MoveKr'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME, SOURCE } from '@/constants/analytics'
import errorCodes from '@/constants/errorCodes'
import { DEFAULT_ERROR } from '@/constants/messages'
import { EXT_LOOKUP, KR_ACTIONS, KR_MEASURING_UNIT } from '@/constants/kr'
import { TOAST_TYPE } from '@/constants/toasts'
import EventBus from '@/EventBus'

const messages = {
  delete: {
    title: 'Delete this KR?',
    body: 'Deleting this KR will delete all related Initiatives. This action cannot be undone.',
    ok: 'Yes, delete'
  },
  unarchive: {
    title: 'Restore this KR?',
    body: "We'll move it to the same goal it belonged to before it was archived.",
    ok: 'Yes, restore'
  },
  archive: {
    title: 'Are you sure you want to archive this KR?',
    body:
      'The Goal will lose the progress contributed by this KR. You can always restore an archived KR.',
    ok: 'Yes, archive'
  },
  forceUnarchive: {
    title: 'There was a problem',
    body:
      'The goal this KR belongs to has been archived. We need to restore the goal first to restore this KR.',
    bodySecondary: '',
    ok: 'Restore Goal and KR',
    cancel: 'Cancel'
  },
  reopen: {
    title: 'Reopen this KR?',
    body: 'Are you sure you want to reopen this KR.',
    ok: 'Yes, re-open'
  }
}

/**
 * This component is for options dropdown for KR Row
 * props:
 *  - kr
 *
 * @emits
 *  - open
 *  - kr-archived
 *  - kr-unarchived
 *  - kr-deleted
 *  - kr-closed
 *  - kr-reopened
 *  - close-checkin
 *  - open-checkin
 *  - moved-kr
 *  - show-award-modal
 */
export default {
  name: 'KrOptions',
  props: ['kr', 'source', 'showKeyboardHints'],
  mixins: [ParentGoalsMixin],
  components: {
    GiveAward,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    ActionKey,
    BulkCheckIn,
    MoveKr,
    ConfirmDialog,
    KrGoogleConnect,
    AllInitiativesNavOption,
    CloseEntity,
    FollowOption
  },
  data() {
    return {
      isBulkCheckinModalShown: false,
      KR_MEASURING_UNIT,
      showMoveKrModal: false,
      showGoogleSheetModal: false,
      EXT_LOOKUP,
      messages,
      confirmationMessage: {},
      action: null,
      isCloseKRModalShown: false,
      isAwardModalShown: false
    }
  },
  computed: {
    goal() {
      return this.kr ? this.$store.getters.goalById(this.kr.goalId) : {}
    },
    team() {
      return this.goal.teamId ? this.$store.getters.teamById(this.goal.teamId) : {}
    },
    owners() {
      return this.kr ? this.kr.ownerships.map(x => x.userId) : []
    },
    awardContext() {
      return {
        ...getEntityAwardContext(this.kr, 'kr'),
        goalId: this.kr.goalId
      }
    }
  },
  methods: {
    ...mapActions(['archiveKr', 'getCurrentUser']),
    editGoal() {
      const query = { kr: this.kr.id, action: KR_ACTIONS.EDIT_KR }
      const location = getEditGoalPageRouteConfig({
        orgId: this.kr.organizationId,
        goalId: this.kr.goalId,
        query
      })
      this.$router.push(location)
    },
    showBulkCheckin() {
      this.$emit('close-checkin')
      this.isBulkCheckinModalShown = true
    },
    async requestUpdate() {
      const params = {
        orgId: this.kr.organizationId,
        goalId: this.kr.goalId,
        krId: this.kr.id
      }
      try {
        await this.$store.dispatch('requestKrUpdate', params)
        EventBus.$emit('ADD_TOAST', { message: 'Your update request is sent to all owners' })
        trackEvent(EVENT_CATEGORY.ACTIVITY, EVENT_ACTION.REQUEST_UPDATE_MENU, EVENT_NAME.KR)
      } catch (e) {
        const message = R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)
        EventBus.$emit('ADD_TOAST', { message, type: TOAST_TYPE.ERROR })
      }
    },
    goToKrPage(event) {
      const location = getKrPageRouteConfig({
        krId: this.kr.id,
        orgId: this.kr.organizationId
      })
      handleClickNavigation.call(this, event, location)
    },
    onKrMove(payload) {
      this.$emit('moved-kr', payload)
      this.showMoveKrModal = false
    },
    async deleteKR() {
      try {
        await this.$store.dispatch('deleteKR', {
          orgId: this.kr.organizationId,
          krId: this.kr.id,
          goalId: this.kr.goalId
        })
        this.$emit('kr-deleted')
      } catch (e) {
        const message = R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)
        EventBus.$emit('ADD_TOAST', { message, type: TOAST_TYPE.ERROR })
      }
    },
    async archive(action = 'archive', force) {
      try {
        await this.archiveKr({
          action,
          id: this.kr.id,
          goalId: this.kr.goalId,
          organizationId: this.kr.organizationId,
          force
        })
        let message = 'KR has been archived',
          event = 'kr-archived'
        if (action === 'unarchive') {
          message = 'KR has been restored'
          event = 'kr-unarchived'
        }
        if (action === 'archive') {
          this.refreshParentGoals(this.kr.goalId) // Did this to make sure that UI is updated with progress of all parent goals
        }
        this.$emit(event)
        EventBus.$emit('ADD_TOAST', {
          message
        })
        return { success: true }
      } catch (e) {
        const response = R.pathOr(DEFAULT_ERROR, ['response', 'data'], e)
        if (response.code) {
          return { response, errorCode: response.code }
        } else {
          const message = R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)
          EventBus.$emit('ADD_TOAST', { message, type: TOAST_TYPE.ERROR })
        }
      }
    },
    async restoreKr(force) {
      const { errorCode, success, response } = await this.archive('unarchive', force)
      if (success) {
        if (force) {
          this.getCurrentUser()
        }
      } else if (errorCode) {
        if (errorCode === errorCodes.teamArchived) {
          this.messages.forceUnarchive.body = `The team (${
            this.team.name
          }) and the goal this KR belongs to have been archived. We need to restore the team and the goal to restore this KR.`
          this.messages.forceUnarchive.ok = 'Restore team, goal and KR'
          this.messages.forceUnarchive.bodySecondary = ''
        } else if (errorCode === errorCodes.goalArchived) {
          this.messages.forceUnarchive.bodySecondary = response.goalTitle
        }
        this.triggerConfirmation('forceUnarchive')
      }
    },
    async performSelectedAction() {
      try {
        const { goalId, organizationId: orgId, id: krId } = this.kr
        await service.client.post(urlManager.GoalKRDetail({ orgId, goalId, krId }), {
          action: this.action
        })
        this.$store.dispatch('getGoal', { goalId, orgId })
        showToast('KR has been re-opened successfully')
      } catch (e) {
        showAPIError(e)
        throw e
      }
    },
    triggerConfirmation(action) {
      this.action = action
      this.confirmationMessage = messages[action]
      this.$refs.confirmModal.$refs.dialog.show()
    },
    async onConfirmation() {
      if (this.action === 'archive') {
        this.archive()
      } else if (this.action === 'unarchive') {
        this.restoreKr()
      } else if (this.action === 'delete') {
        this.deleteKR()
      } else if (this.action === 'forceUnarchive') {
        this.restoreKr(true)
      } else if (this.action === 'reopen') {
        await this.performSelectedAction()
        this.$emit('kr-reopened')
      }
    },
    fetchGoal() {
      return this.getGoal({
        orgId: this.kr.organizationId,
        goalId: this.kr.goalId
      })
    },
    showAwardModal() {
      this.isAwardModalShown = true
    },
    showKrActivityModal() {
      showModal({
        component: KrActivityModal,
        id: `${ENTITY_TYPE.KEY_RESULT}-${this.kr.id}`,
        props: {
          show: true,
          krId: this.kr.id,
          showCheckin: true,
          source: this.source === SOURCE.KR_PAGE ? SOURCE.KR_PAGE_BUTTON : this.source
        },
        on: {}
      })
    },
    async openCheckinModal() {
      if (!this.goal) {
        await this.fetchGoal()
      }
      this.showKrActivityModal()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/components/_optionsMenu.scss';

.award-icon {
  @include dimension(20px, 20px);
  background-size: contain;
}

@include media-breakpoint-down(md) {
  .edit-menu {
    display: none;
  }
}
</style>
